// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@keller/App/Components/Common/XngZone";
import bannerPageLayoutSharedStyles from "@keller/App/SharedStyle/Layouts/BannerPageLayoutSharedStyles";
import { color, xScreen } from "@keller/XDL/Utilities";
import { mergeWithCss } from "@keller/XDL/Utilities/mergeWithCss";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

const enum fields {
    gap = "gap",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.gap]: number;
    };
}

interface BrandPageLayoutStyles {
    wrapper?: InjectableCss;
    bannerSection?: InjectableCss;
    bannerWrapper?: InjectableCss;
    infoSection?: InjectableCss;
    mainSection?: InjectableCss;
    logoActionsSection?: InjectableCss;
}

const brandPageLayoutStyles: BrandPageLayoutStyles = {
    bannerSection: {
        ...mergeWithCss(bannerPageLayoutSharedStyles.bannerSection, {
            css: css`
                ${({ backgroundImage }: any) =>
                    backgroundImage &&
                    css`
                        ${xScreen("gt", "sm")} {
                            background: ${color("common.ui12")};
                        }
                    `};
            `,
        }),
    },
    bannerWrapper: {
        ...mergeWithCss(bannerPageLayoutSharedStyles.bannerWrapper, {
            css: css`
                background-size: contain;
            `,
        }),
    },
    infoSection: {
        ...bannerPageLayoutSharedStyles.infoSection,
    },
    mainSection: {
        ...bannerPageLayoutSharedStyles.mainSection,
    },
    logoActionsSection: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-bottom: 16px;
            > div {
                margin-right: 8px;
            }

            ${xScreen("lte", "sm")} {
                flex-direction: column;

                > div {
                    width: 100%;
                    margin-right: 0;
                }
            }
        `,
    },
};

const styles = brandPageLayoutStyles;

export const BrandPageLayout: FC<OwnProps> = ({ id, fields: { gap } }) => {
    const { value: brand } = useContext(BrandStateContext);

    const styledProps = {
        gap,
        backgroundImage: brand?.featuredImagePath,
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.bannerSection} {...styledProps} data-test-selector="Banner">
                <StyledWrapper {...styles.bannerWrapper} {...styledProps}>
                    <StyledWrapper {...styles.infoSection} {...styledProps}>
                        <PageBreadcrumbs />
                        {createWidgetElement("BrandDetails/BrandDetailsLogo", {
                            fields: {
                                showTitleOnly: true,
                            },
                        })}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.mainSection} data-test-selector="MainSection">
                <StyledWrapper {...styles.logoActionsSection}>
                    <XngZone contentId={id} zoneName="Content01" hint="Logo Section" />
                    <XngZone contentId={id} zoneName="Content02" hint="Links Section" normalWidth={true} />
                </StyledWrapper>
                <XngZone contentId={id} zoneName="Content03" hint="Description Section" />
                <XngZone contentId={id} zoneName="Content04" hint="Shop by category Section" />
                <XngZone contentId={id} zoneName="Content05" hint="Product lines Section" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BrandPageLayout,
    definition: {
        group: "Brand Details",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.gap,
                displayName: "Gap",
                editorTemplate: "IntegerField",
                defaultValue: 48,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
